// @ts-nocheck
/**
 * Public Portfolio Page
 * Non-authenticated public portfolio viewing interface
 * Epic 75 - Work Item 3: R2 Object Storage and Content Delivery Architecture
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Column,
  Tag,
  Button,
  SkeletonPlaceholder,
  SkeletonText,
  InlineNotification,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Tile,
  ClickableTile,
} from '@carbon/react';
import {
  Portfolio,
  View,
  Download,
  Share,
  ThumbsUp,
  Email,
  LogoLinkedin,
  LogoGithub,
  User,
  Calendar,
  Education,
  Certificate,
} from '@carbon/react/icons';

interface PublicPortfolioItem {
  id: string;
  title: string;
  description: string;
  category: string;
  fileUrl: string;
  thumbnailUrl?: string;
  tags: string[];
  viewCount: number;
  likeCount: number;
  createdAt: string;
  featured: boolean;
}

interface PublicProfile {
  userId: string;
  username?: string;
  displayName: string; // Backend uses displayName instead of name
  name?: string; // Fallback for name
  title?: string;
  bio: string;
  profileImageUrl?: string; // Backend uses profileImageUrl instead of avatarUrl
  avatarUrl?: string; // Fallback for avatarUrl
  location?: string;
  website?: string;
  education?: string;
  skills: string[];
  socialLinks?: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    dribbble?: string;
    website?: string;
    email?: string;
  };
  stats: {
    totalProjects: number;
    totalViews: number;
    totalLikes: number;
    memberSince: string; // Backend uses memberSince instead of joinDate
    joinDate?: string; // Fallback for joinDate
  };
  isPublic?: boolean;
  lastActive?: string;
}

const PublicPortfolio: React.FC = () => {
  const { user_id, username } = useParams<{ user_id?: string; username?: string }>();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [portfolioItems, setPortfolioItems] = useState<PublicPortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [likedItems, setLikedItems] = useState<Set<string>>(new Set());

  const fetchPortfolio = async () => {
    setLoading(true);
    try {
      // Determine the identifier to use (user_id or username)
      const identifier = user_id || username;
      if (!identifier) {
        throw new Error('Portfolio identifier not provided');
      }

      // Use correct API endpoints based on validation results
      // Backend implements /api/v1/public/${userId}/profile, not /api/v1/public/portfolios/${user_id}/profile
      const profileResponse = await fetch(`/api/v1/public/${identifier}/profile`);
      if (!profileResponse.ok) {
        throw new Error('Portfolio not found');
      }
      const profileData = await profileResponse.json();
      // Handle both wrapped and unwrapped response formats
      setProfile(profileData.profile || profileData.data || profileData);

      // Fetch portfolio items using correct endpoint
      const itemsResponse = await fetch(`/api/v1/public/${identifier}/items`);
      if (!itemsResponse.ok) {
        throw new Error('Failed to fetch portfolio items');
      }
      const itemsData = await itemsResponse.json();
      setPortfolioItems(itemsData.items || itemsData);

      // Load liked items from localStorage
      const storedLikes = localStorage.getItem(`portfolio_likes_${identifier}`);
      if (storedLikes) {
        setLikedItems(new Set(JSON.parse(storedLikes)));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const identifier = user_id || username;
    if (identifier) {
      fetchPortfolio();
      // Track view using correct endpoint
      fetch(`/api/v1/public/${identifier}/view`, { method: 'POST' });
    }
  }, [user_id, username]);

  const handleLike = async (itemId: string) => {
    try {
      const isLiked = likedItems.has(itemId);
      const method = isLiked ? 'DELETE' : 'POST';

      const response = await fetch(`/api/v1/public/items/${itemId}/like`, {
        method,
      });

      if (!response.ok) {
        throw new Error('Failed to update like');
      }

      const newLikedItems = new Set(likedItems);
      if (isLiked) {
        newLikedItems.delete(itemId);
      } else {
        newLikedItems.add(itemId);
      }

      setLikedItems(newLikedItems);
      const identifier = user_id || username;
      localStorage.setItem(`portfolio_likes_${identifier}`, JSON.stringify(Array.from(newLikedItems)));

      // Update like count locally
      setPortfolioItems(prev =>
        prev.map(item => (item.id === itemId ? { ...item, likeCount: item.likeCount + (isLiked ? -1 : 1) } : item))
      );
    } catch (error) {
      console.error('Like error:', error);
    }
  };

  const handleShare = async (item?: PublicPortfolioItem) => {
    const shareUrl = item ? `${window.location.href}#${item.id}` : window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: item ? item.title : `${profile?.displayName || profile?.name}'s Portfolio`,
          text: item ? item.description : profile?.bio,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      project: 'blue',
      certification: 'green',
      achievement: 'purple',
      article: 'teal',
      presentation: 'cyan',
    };
    return colors[category] || 'gray';
  };

  const getFilteredItems = (filter: string) => {
    switch (filter) {
      case 'featured':
        return portfolioItems.filter(item => item.featured);
      case 'projects':
        return portfolioItems.filter(item => item.category === 'project');
      case 'certifications':
        return portfolioItems.filter(item => item.category === 'certification');
      default:
        return portfolioItems;
    }
  };

  if (loading) {
    return (
      <div className="public-portfolio-page bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-8">
          <Grid>
            <Column lg={4} md={8} sm={4}>
              <SkeletonText paragraph lineCount={6} />
            </Column>
            <Column lg={12} md={8} sm={4}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <SkeletonPlaceholder key={i} style={{ height: '300px', marginBottom: '16px' }} />
                ))}
              </div>
            </Column>
          </Grid>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="public-portfolio-page bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-8">
          <InlineNotification
            kind="error"
            title="Portfolio not found"
            subtitle={error || 'This portfolio may be private or does not exist'}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="public-portfolio-page bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-12">
          <Grid>
            <Column lg={16} md={8} sm={4}>
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {profile.profileImageUrl || profile.avatarUrl ? (
                    <img
                      src={profile.profileImageUrl || profile.avatarUrl}
                      alt={profile.displayName || profile.name}
                      className="w-32 h-32 rounded-full border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-white bg-opacity-20 flex items-center justify-center border-4 border-white shadow-lg">
                      <User size={48} />
                    </div>
                  )}
                </div>

                {/* Profile Info */}
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-4xl font-bold mb-2">{profile.displayName || profile.name}</h1>
                  <p className="text-xl mb-4 opacity-90">{profile.title || 'Creative Professional'}</p>
                  <p className="text-base mb-6 opacity-80 max-w-2xl">{profile.bio}</p>

                  {/* Social Links */}
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    {profile.socialLinks?.linkedin && (
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={LogoLinkedin}
                        href={profile.socialLinks.linkedin}
                        target="_blank"
                        className="text-white hover:bg-white hover:bg-opacity-20"
                      >
                        LinkedIn
                      </Button>
                    )}
                    {profile.socialLinks?.github && (
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={LogoGithub}
                        href={profile.socialLinks.github}
                        target="_blank"
                        className="text-white hover:bg-white hover:bg-opacity-20"
                      >
                        GitHub
                      </Button>
                    )}
                    {profile.socialLinks?.email && (
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={Email}
                        href={`mailto:${profile.socialLinks.email}`}
                        className="text-white hover:bg-white hover:bg-opacity-20"
                      >
                        Contact
                      </Button>
                    )}
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Share}
                      onClick={() => handleShare()}
                      className="text-white hover:bg-white hover:bg-opacity-20"
                    >
                      Share Portfolio
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex md:flex-col gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold">{profile.stats.totalProjects}</div>
                    <div className="text-sm opacity-75">Projects</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">{profile.stats.totalViews.toLocaleString()}</div>
                    <div className="text-sm opacity-75">Views</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">{profile.stats.totalLikes.toLocaleString()}</div>
                    <div className="text-sm opacity-75">Likes</div>
                  </div>
                </div>
              </div>
            </Column>
          </Grid>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Grid>
          {/* Sidebar */}
          <Column lg={4} md={8} sm={4} className="mb-6">
            <div className="sticky top-4 space-y-4">
              {/* About Card */}
              <Tile>
                <h3 className="font-semibold mb-3">About</h3>
                <div className="space-y-3 text-sm">
                  {profile.location && (
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-gray-500" />
                      <span>{profile.location}</span>
                    </div>
                  )}
                  {profile.education && (
                    <div className="flex items-center gap-2">
                      <Education size={16} className="text-gray-500" />
                      <span>{profile.education}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-500" />
                    <span>
                      Joined{' '}
                      {new Date(profile.stats.memberSince || profile.stats.joinDate || new Date()).toLocaleDateString(
                        'en-US',
                        {
                          month: 'long',
                          year: 'numeric',
                        }
                      )}
                    </span>
                  </div>
                </div>
              </Tile>

              {/* Skills Card */}
              {profile.skills.length > 0 && (
                <Tile>
                  <h3 className="font-semibold mb-3">Skills</h3>
                  <div className="flex flex-wrap gap-1">
                    {profile.skills.map(skill => (
                      <Tag key={skill} kind="blue" size="sm">
                        {skill}
                      </Tag>
                    ))}
                  </div>
                </Tile>
              )}
            </div>
          </Column>

          {/* Portfolio Items */}
          <Column lg={12} md={8} sm={4}>
            <Tabs selectedIndex={activeTab} onChange={(e: any) => setActiveTab(e.selectedIndex)}>
              <TabList aria-label="Portfolio sections">
                <Tab>All ({portfolioItems.length})</Tab>
                <Tab>Featured ({getFilteredItems('featured').length})</Tab>
                <Tab>Projects ({getFilteredItems('projects').length})</Tab>
                <Tab>Certifications ({getFilteredItems('certifications').length})</Tab>
              </TabList>
              <TabPanels>
                {['all', 'featured', 'projects', 'certifications'].map((filter, index) => (
                  <TabPanel key={filter}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                      {getFilteredItems(filter).map(item => (
                        <ClickableTile
                          key={item.id}
                          className="portfolio-card hover:shadow-lg transition-shadow"
                          onClick={() => {}}
                        >
                          {/* Card Header with Image */}
                          <div className="relative">
                            {item.thumbnailUrl ? (
                              <img
                                src={item.thumbnailUrl}
                                alt={item.title}
                                className="w-full h-48 object-cover rounded-t"
                              />
                            ) : (
                              <div className="w-full h-48 bg-gray-100 flex items-center justify-center rounded-t">
                                <Portfolio size={48} />
                              </div>
                            )}
                            {item.featured && (
                              <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                Featured
                              </div>
                            )}
                          </div>

                          {/* Card Content */}
                          <div className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="text-lg font-semibold">{item.title}</h3>
                              <Tag kind={getCategoryColor(item.category) as any} size="sm">
                                {item.category}
                              </Tag>
                            </div>

                            <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                              {item.description || 'No description provided'}
                            </p>

                            {/* Tags */}
                            {item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {item.tags.slice(0, 3).map(tag => (
                                  <Tag key={tag} kind="gray" size="sm">
                                    {tag}
                                  </Tag>
                                ))}
                              </div>
                            )}

                            {/* Stats */}
                            <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                              <span>{item.viewCount} views</span>
                              <span>{item.likeCount} likes</span>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                              <Button
                                kind="primary"
                                size="sm"
                                renderIcon={View}
                                href={item.fileUrl}
                                target="_blank"
                                className="flex-1"
                              >
                                View
                              </Button>
                              <Button
                                kind={likedItems.has(item.id) ? 'danger' : 'secondary'}
                                size="sm"
                                hasIconOnly
                                renderIcon={ThumbsUp}
                                iconDescription="Like"
                                onClick={() => handleLike(item.id)}
                              />
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={Share}
                                iconDescription="Share"
                                onClick={() => handleShare(item)}
                              />
                            </div>
                          </div>
                        </ClickableTile>
                      ))}
                    </div>

                    {getFilteredItems(filter).length === 0 && (
                      <div className="text-center py-12">
                        <Portfolio size={48} className="mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-500">No items found in this category</p>
                      </div>
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Column>
        </Grid>
      </div>
    </div>
  );
};

export default PublicPortfolio;
